import { useCallback, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import newIdAttributesGenerator from "../../../../helpers/newIdAttributesGenerator";
import {
  useCheckDuplicateAttribute,
  useCreateAttributes,
  useGetAttributes,
} from "../../../../hooks/api/attributes";
import { handleFormatAttributePostPayload } from "../../../../helpers/Asset";

const NEW_ATTRIBUTES_LIST = [
  {
    detail: "",
    id: "",
    fieldType: {
      label: "Text",
      value: "text-entry",
    },
    group: "Custom",
  },
];

const useAddAttributesPopup = ({ popupData, closePopup }) => {
  const {
    resource,
    onAddElements,
    onSubmit,
    isAdminPage = false,
  } = popupData?.item;
  const { attributes, attributesLoading, refetchAttributes } =
    useGetAttributes();

  const { createAttributes } = useCreateAttributes();
  // Resources already on the current Resource ( P/P/A )
  const resourceAttributes = cloneDeep(resource?.additionalInfo);
  //  List of Attributes in list under Choose Existing
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  // Newly Created Attribute
  const [newAttributes, setNewAttributes] = useState(NEW_ATTRIBUTES_LIST);

  const { checkDuplicateAttribute, isDuplicate } = useCheckDuplicateAttribute();

  // Change Attributes in the list of attributes to add to resource
  const handleChangeSelectedAttribute = (val, uId, oId, id) => {
    const elementIndex = selectedAttributes.findIndex(
      (item) => item?.id === id
    );

    if (elementIndex > -1) {
      setSelectedAttributes((prev) => {
        const result = prev.map((item, idx) => {
          if (idx === elementIndex) {
            const value = val ?? item.data?.value;
            const unitId = uId ?? item.data?.unitId;
            const optionId = oId ?? item.data?.optionId;
            return {
              ...item,
              data: {
                id,
                ...(value && { value }),
                ...(unitId && { unitId }),
                ...(optionId && { optionId }),
              },
            };
          }
          return item;
        });

        return result;
      });
    }
  };

  // Attributes available in dropdown list
  const formatOptionsForDDL = (
    attributesList,
    resourceAttributesList,
    currentSelectedAttributes
  ) => {
    let options = [];
    if (attributesList?.length > 0 && !attributesLoading) {
      options = [...attributesList];
      if (resourceAttributesList?.length > 0) {
        // remove already in-use attributes from list of options
        const inUseIds = resourceAttributesList?.map((item) => item?.id);
        const inUseIdsSet = new Set(inUseIds);
        const availableAttributeOptions = options?.filter(
          (item) => !inUseIdsSet.has(item.id)
        );
        options = availableAttributeOptions;
      }
      // remove attributes from list that are selected in the modal
      if (currentSelectedAttributes?.length > 0) {
        const inUseIds = currentSelectedAttributes?.map((item) => item?.id);

        const inUseIdsSet = new Set(inUseIds);
        const updatedOptions = options?.filter(
          (item) => !inUseIdsSet.has(item.id)
        );
        options = updatedOptions;
      }
      if (options?.length > 0) {
        options = options.map((item) => {
          const updatedOption = {
            label: item?.detail || item?.name,
            value: item?.id,
          };
          return updatedOption;
        });

        return options;
      }
    }
    return options;
  };

  // Selecting Attribute from dropdown of current attributes and after creation
  const handleSelectAttribute = useCallback(
    (selectedAttribute) => {
      let formattedAttribute;
      // if selected attribute has just been created it will be the full attribute response
      // if selected from drop down list it will be value: uuid and need to be found in attributeList
      formattedAttribute = selectedAttribute;

      const attributesList = cloneDeep(attributes);
      const foundAttribute = attributesList?.find(
        (item) => item.id === selectedAttribute.value
      );

      // if attribute is already available in list
      if (foundAttribute) {
        formattedAttribute = cloneDeep(foundAttribute);
      }

      formattedAttribute.data = {};
      if (formattedAttribute.fieldType === "check-box") {
        formattedAttribute.data.value = "No";
      }
      setSelectedAttributes((prev) => [...prev, formattedAttribute]);
    },
    [attributes]
  );

  // Remove Attribute from list of Attributes to add to the Resource
  const handleRemoveAttribute = (detail) => {
    setSelectedAttributes((prev) =>
      prev.filter((item) => item.detail !== detail)
    );
  };

  // Change Attribute in the "Create New"
  const onChangeAttribute = (field, value, index) => {
    setNewAttributes((prev) =>
      prev.map((attribute, idx) => {
        if (idx === index) {
          if (field === "options") {
            return {
              ...attribute,
              options: value?.map((opt) => ({
                label: opt?.label,
                value: newIdAttributesGenerator(opt),
              })),
            };
          }
          if (field === "fieldType" && value?.hasUnits) {
            return {
              ...attribute,
              fieldType: value,
              unit: {
                fieldType: "ddl",
                options: [],
              },
              options: [],
            };
          }
          if (field === "unit") {
            return {
              ...attribute,
              unit: {
                ...attribute?.unit,
                options: value?.map((opt) => ({
                  label: opt?.label,
                  value: newIdAttributesGenerator(opt),
                })),
              },
            };
          }
          if (field === "detail") {
            checkDuplicateAttribute(value);
            return {
              ...attribute,
              detail: value,
            };
          }
          return {
            ...attribute,
            [`${field}`]: value,
            unit: null,
            options: [],
          };
        }
        return attribute;
      })
    );
  };
  // Clear "Create New" field
  const handleClearNewEntry = () => {
    setNewAttributes(NEW_ATTRIBUTES_LIST);
  };

  // Add button in "Create New"
  const onAddNewAttribute = useCallback(async () => {
    if (isAdminPage) {
      const formatAttr = handleFormatAttributePostPayload(newAttributes[0]);
      await onSubmit(formatAttr);
      handleClearNewEntry();
      return;
    }
    try {
      // Format Payload for Post
      const formattedAttribute = handleFormatAttributePostPayload(
        newAttributes[0]
      );
      // Format data for RQ post
      const createdItems = [{ ...formattedAttribute, wasAdded: true }];
      const props = { args: { createdItems } };
      const response = await createAttributes(props);
      // if response -> select attribute to put in list
      if (response && !isAdminPage) {
        // Posting to Attribute.API uses $bulkcreate and returns array, this screen only allows posting one at a time.
        handleSelectAttribute({ ...response[0] });
      }
    } catch (e) {
      console.error(
        "Error creating new attribute in useAddAttributesPopup.js",
        e
      );
    }
    handleClearNewEntry();
  }, [
    createAttributes,
    handleSelectAttribute,
    isAdminPage,
    newAttributes,
    onSubmit,
  ]);

  // Resource expects [ { id: attributeId, value: "" || Number || bool , unitID || optionID} ]
  const formatAttributeForResource = (list) => {
    const formattedList = [];
    if (list?.length > 0) {
      list.forEach((singleAttribute) => {
        const resourcePayload = {
          id: singleAttribute.id,
          value: singleAttribute.data.value,
          ...singleAttribute,
        };
        formattedList.push(resourcePayload);
      });
    }
    return formattedList;
  };
  // Primary Save Function if isAdmin this will not be used
  const onFinish = () => {
    const resourcePayloadArray = formatAttributeForResource(selectedAttributes);

    // returning the list of new attributes to add to the P/P/A
    if (!isAdminPage) {
      onAddElements(resourcePayloadArray);
    }

    refetchAttributes();

    // clear popup state
    setSelectedAttributes([]);
    closePopup();
  };

  // close modal, clear state
  const handleCancel = () => {
    closePopup();
    setSelectedAttributes([]);
  };

  const isValidNewAttribute = () => {
    if (!newAttributes?.length) return false;

    return newAttributes.every((attribute) => {
      let isValid = true;

      if (!attribute?.detail || attribute?.detail === "") {
        isValid = false;
      }

      if (!attribute?.fieldType?.value) {
        isValid = isValid && false;
      }

      if (attribute?.fieldType?.value === "ddl") {
        isValid = isValid && !!attribute?.options?.length;
      }

      if (attribute?.fieldType?.value === "number-entry-with-unit") {
        isValid = !!attribute?.unit?.options?.length;
      }

      return isValid && !isDuplicate;
    });
  };

  const canAddAttributes = useCallback(() => {
    const allowAdd = selectedAttributes?.every((s) => {
      if (s.fieldType === "text-entry") {
        if (s?.options?.length > 0) return !!s.data.unitId && !!s.data.value;
        return !!s.data.value;
      }
      if (s.fieldType === "number-entry") {
        if (s?.unit?.options?.length > 0)
          return !!s.data.unitId && !!s.data.value;
        return !!s.data.value && !Number.isNaN(Number(s.data.value));
      }
      if (s.fieldType === "number-entry-with-unit") {
        return !!s.data.unitId && !Number.isNaN(Number(s.data.value));
      }
      if (s.fieldType === "check-box") return !!s.data.value;
      if (s.fieldType === "date-picker") return !!s.data.value;
      if (s.fieldType === "ddl")
        return !!s.data.optionId && !!s?.options?.length > 0;

      return false;
    });

    return selectedAttributes?.length > 0 ? allowAdd : false;
  }, [selectedAttributes]);

  return {
    // current attribute options
    attributes,
    resourceAttributes,
    formatOptionsForDDL,
    attributesLoading,
    // selected attributes from the current options
    selectedAttributes,
    // select/remove/change/validation from temporary list of attributes to send to resource
    handleSelectAttribute,
    handleRemoveAttribute,
    handleChangeSelectedAttribute,
    canAddAttributes,
    // Create New section, onChange, state, clear, validation
    onAddNewAttribute,
    onChangeAttribute,
    newAttributes,
    handleClearNewEntry,
    isValidNewAttribute,
    isDuplicate,
    // utility, main cancel & onFinish
    isAdminPage,
    handleCancel,
    onFinish,
  };
};

export default useAddAttributesPopup;
